import React, { useRef, useState, useCallback } from "react";
import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import SelectComponent from "../../components/SelectComponent/SelectComponent";
import Addguest from "./Addguest";
import lang from "../../assets/lang.png";
import DateComponent from "./DateComponent";
import ReservationType from "./ReservationType";
import TimeComponent from "./TimeComponent";
import { updateDefaultLanguage } from "../../store/slice/Activity.slice";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import calenderImg from "../../assets/CalendarAct.png";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatTimeString, format_Number } from "../../utils/utils";
import InactivityPopup from "../../utils/InActivityPopup";
import { setLanguageID } from "../../store/slice/Addguest.slice";
import LanguagePopover from "../Component/LanguagePopover";
import { WidgetHeightProvider, useWidgetHeight } from "../horecaWidget/widgetHeightContext";

dayjs?.extend(customParseFormat);

// ─── inner content (consumes the height context) ──────────────────────────────
const WidgetContent = ({ activityData }) => {
  const IMG_URL   = process.env.REACT_APP_API_LOCAL_IMG_URL;
  const deviceId  = JSON.parse(localStorage.getItem("deviceId"));

  const { widgetConfig, loading, dispatch, theme } = activityData;

  const { onPopoverOpen, onPopoverClose } = useWidgetHeight();

  const { languageData, key, widgetSetupId, resType, css } =
    useSelector((store) => store?.widgetSettings);

  const bookBtnCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Book button"
  )?.fields;

  const {
    totalCount, selectedDate, reservType, time,
    defaultLanguage, languageWords, activityDuration,
  } = useSelector((store) => store.Activity);

  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const hasImage        = Boolean(widgetConfig?.settings?.imageFileName?.trim());
  const widgetPosition  = "Bottom"; // locked per original

  // ── refs ──────────────────────────────────────────────────────────────────
  const guestRef            = useRef(null);
  const dateRef             = useRef(null);
  const reservationTypeRef  = useRef(null);
  const timeRef             = useRef(null);
  const activityDurationRef = useRef(null);

  // ── popover open/close state ──────────────────────────────────────────────
  const [isAddGuest,         setIsAddGuest]         = useState(false);
  const [isDateOpen,         setIsDateOpen]         = useState(false);
  const [isResTypeOpen,      setIsResTypeOpen]      = useState(false);
  const [isTimeOpen,         setIsTimeOpen]         = useState(false);
  const [isActivityDuration, setIsActivityDuration] = useState(false);

  // ── helpers ───────────────────────────────────────────────────────────────
  const blur = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  const closeAll = useCallback((except) => {
    if (except !== "guest")    setIsAddGuest(false);
    if (except !== "date")     setIsDateOpen(false);
    if (except !== "resType")  setIsResTypeOpen(false);
    if (except !== "time")     setIsTimeOpen(false);
    if (except !== "duration") setIsActivityDuration(false);
  }, []);

  const toggle = (setter, key, isOpen) => () => {
    blur();
    if (isOpen) { setter(false); onPopoverClose(); }
    else        { closeAll(key); setter(true);  onPopoverOpen(); }
  };

  const close = (setter) => () => { blur(); setter(false); onPopoverClose(); };

  // ── language ──────────────────────────────────────────────────────────────
  const handleLanguageChange = (langCode) => {
    const selected = languageData?.find((item) => item.langcode === langCode);
    if (!selected) return;
    dispatch(updateDefaultLanguage(selected.langcode));
    dispatch(setLanguageID(selected.idlanguage));
    localStorage.setItem("widget", JSON.stringify({
      id: widgetSetupId, key, lang: selected.langcode, resType,
    }));
  };

  // ── derived ───────────────────────────────────────────────────────────────
  const isBookActive =
    reservType && typeof reservType === "object" &&
    Object.keys(reservType).length > 0 &&
    totalCount > 0 &&
    selectedDate?.trim() !== "" &&
    time?.trim() !== "";

  const commonSelectSx = {
    width: "100%",
    height: "72px",
    WebkitTapHighlightColor: "transparent",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "& .MuiOutlinedInput-input": {
      padding: 1,
      color: theme?.guest?.textColor,
      ml: 3, mt: 1,
      fontSize: { xs: "12px", md: "14px" },
    },
  };

  const imageBgSx = hasImage
    ? {
        backgroundImage: `url(${IMG_URL}/upload/Widgetsetup${widgetConfig.settings.imageFileName.replace(/\\/g, "/")})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: widgetConfig?.settings?.imageHeight
          ? `${widgetConfig.settings.imageHeight}px`
          : "100vh",
      }
    : { backgroundColor: theme.widget?.mainBg };

  const panelSx = hasImage
    ? {
        position: "absolute", left: 0, right: 0, zIndex: 1,
        ...(widgetPosition === "Top"    && { top: 0 }),
        ...(widgetPosition === "Bottom" && { bottom: 0 }),
        ...(widgetPosition === "Middle" && { top: "50%", transform: "translateY(-50%)" }),
      }
    : { position: "relative" };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        overflowX: "hidden",
        ...imageBgSx,
      }}
    >
      {deviceId && <InactivityPopup languageWords={languageWords} />}

      <Box sx={{ ...panelSx, width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
          }}
        >
          {/* Language + Close */}
          {languageData?.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <LanguagePopover
                  langIcon={lang}
                  languageData={languageData}
                  selectedLanguage={defaultLanguage}
                  onChange={handleLanguageChange}
                  theme={theme}
                  defaultLanguage={defaultLanguage}
                />
                {widgetConfig?.settings?.floatButtonActive &&
                  !widgetConfig?.settings?.kioskMode && (
                  <IconButton
                    size="large"
                    onClick={activityData.handleClose}
                    sx={{
                      backgroundColor: theme?.widgetButton?.closeBg,
                      minWidth: { xs: 44, sm: "auto" },
                      minHeight: { xs: 44, sm: "auto" },
                      "&:hover": { backgroundColor: theme?.widgetButton?.closeHover },
                    }}
                  >
                    <CloseIcon sx={{ color: theme?.widgetButton?.closeText }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {totalCount >= widgetConfig?.reservationRequestMin && (
            <Box sx={{ color: theme?.widget?.reservGrpTextClr, mb: 1, fontSize: { xs: "13px", sm: "14px" } }}>
              {languageWords?.reservationGroupText}
            </Box>
          )}

          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              color: theme?.widget?.titleTextClr,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {languageWords?.widgetHeading || "Start uw reservering"}
          </Typography>

          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch" sx={{ position: "relative" }}>

            {/* Guests */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.Guest}
                value={totalCount}
                onClick={toggle(setIsAddGuest, "guest", isAddGuest)}
                ref={guestRef}
                sx={commonSelectSx}
              />
              <Addguest
                isOpen={isAddGuest}
                handleClose={close(setIsAddGuest)}
                Ref={guestRef}
                activityData={activityData}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.date}
                value={selectedDate?.trim() !== "" ? selectedDate : "DD-MM-YYYY"}
                onClick={toggle(setIsDateOpen, "date", isDateOpen)}
                ref={dateRef}
                icon={calenderImg}
                isIcon={true}
                disabled={totalCount <= 0}
                sx={commonSelectSx}
              />
              <DateComponent
                isOpen={isDateOpen}
                handleClose={close(setIsDateOpen)}
                Ref={dateRef.current}
              />
            </Grid>

            {/* Reservation Type */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.reserveType}
                value={
                  reservType && typeof reservType === "object" &&
                  Object.keys(reservType).length > 0
                    ? reservType?.name
                    : "Type"
                }
                onClick={toggle(setIsResTypeOpen, "resType", isResTypeOpen)}
                ref={reservationTypeRef}
                disabled={!selectedDate}
                sx={commonSelectSx}
              />
              <ReservationType
                isOpen={isResTypeOpen}
                handleClose={close(setIsResTypeOpen)}
                Ref={reservationTypeRef.current}
              />
            </Grid>

            {/* Duration (conditional) */}
            {reservType?.isPriceBasedOnDuration && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectComponent
                  label={languageWords?.duration || "Duration"}
                  value={
                    activityDuration
                      ? `${formatTimeString(activityDuration?.timeText, languageWords)}  -  € ${format_Number(activityDuration?.price)}`
                      : "HH:MM"
                  }
                  onClick={toggle(setIsActivityDuration, "duration", isActivityDuration)}
                  ref={activityDurationRef}
                  disabled={
                    !selectedDate ||
                    !(reservType && typeof reservType === "object" && Object.keys(reservType).length > 0)
                  }
                  sx={commonSelectSx}
                />
                <TimeComponent
                  isOpen={isActivityDuration}
                  handleClose={close(setIsActivityDuration)}
                  Ref={activityDurationRef.current}
                  isDuration={true}
                />
              </Grid>
            )}

            {/* Time */}
            <Grid item xs={12} sm={6} md={4} lg={reservType?.isPriceBasedOnDuration ? 1.5 : 2}>
              <SelectComponent
                label={languageWords?.Time}
                value={time?.trim() !== "" ? time?.substring(0, 5) : "HH:MM"}
                onClick={toggle(setIsTimeOpen, "time", isTimeOpen)}
                ref={timeRef}
                disabled={
                  !(reservType && typeof reservType === "object" && Object.keys(reservType).length > 0) ||
                  (!activityDuration && reservType?.isPriceBasedOnDuration)
                }
                sx={commonSelectSx}
              />
              <TimeComponent
                isOpen={isTimeOpen}
                handleClose={close(setIsTimeOpen)}
                Ref={timeRef.current}
              />
            </Grid>

            {/* Book Now */}
            <Grid item xs={12} sm={6} md={4} lg={reservType?.isPriceBasedOnDuration ? 1.5 : 2}>
              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  height: "72px",
                  backgroundColor: isBookActive
                    ? bookBtnCss?.BookNowButtonBackgroundColorActive
                    : bookBtnCss?.BookNowButtonBackgroundColor,
                  color: isBookActive
                    ? bookBtnCss?.BookNowButtonTextColorActive
                    : bookBtnCss?.BookNowButtonTextColor,
                  fontWeight: "bold",
                  fontSize: { xs: "13px", sm: "14px" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "none",
                  borderRadius: "8px",
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                  "&:hover": { boxShadow: "none" },
                  "&:active": { transform: "scale(0.98)" },
                }}
                onClick={() => {
                  if (isBookActive && !loading) navigate("/bookNowActivity");
                }}
              >
                {languageWords?.Booknow}
                {reservType?.products?.price > 0 && (
                  <Box component="span" fontSize="13px" fontWeight="200">
                    €{reservType?.products?.price}
                  </Box>
                )}
              </Button>
            </Grid>

          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

// ─── public wrapper (owns the height provider) ────────────────────────────────
const WidgetComponent = ({ activityData, heightChange }) => {
  const hasImage = Boolean(activityData?.widgetConfig?.settings?.imageFileName?.trim());
  return (
    <WidgetHeightProvider onHeightChange={heightChange} disabled={hasImage}>
      <WidgetContent activityData={activityData} />
    </WidgetHeightProvider>
  );
};

export default WidgetComponent;